import React, { useEffect, useMemo, useState } from 'react';

import { Box } from '@chakra-ui/react';
import { ReviewsMobileViewCell } from 'Deer/components/Reviews/ReviewsMobileViewCell';
import { getDeerReviewFormRoute, getDeerReviewResultRoute } from 'Deer/constants/nonDeerRoutes';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { useToast } from 'components/hooks/useToast';
import { format, parseISO, isValid } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { CustomHeaderCell } from 'screens/Team/Rabbit/Goals/AgGridTreeView/components/CustomHeaderCell';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import 'styles/agGrid.css';
import { agGridDateComparator } from 'utils/helpers/dateHelpers';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { CheckCircle, CloseCircle } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/noReviews.svg';

import {
  MODULE_KEYS_USER_PREFERENCE_PERSISTENCE,
  REVIEW_FORM_COMPLETION_STATUS,
  REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP,
  REVIEW_SUBMISSION_STATUS_MAP,
} from 'constants/ProjectConstants';
import {
  downloadOrOpenFile,
  getPersistenceKey,
  getReviewFormRoute,
  getReviewResultRoute,
  noOp,
} from 'constants/helper';
import { isMobileView } from 'constants/isMobileView';
import { sendOpenedNominationPanelEvent, sendReviewFormPageViewEvent } from 'constants/mixpanelHelper';
import {
  sendClickedOnAcknowledgeEvent,
  sendClickedOnContinueReviewEvent,
  sendClickedOnEditNominationsEvent,
  sendClickedOnNominateEvent,
  sendClickedOnStartReviewEvent,
} from 'constants/mixpanelHelpers/reviewsEvents';

import { showNominatePeers } from 'services/nominatePeers';

import { AgGridTable } from 'uiToolkitV1/AgGridWrapperV2';
import { Avatar } from 'uiToolkitV1/Avatar';
import { BUTTON_VARIANT, Button } from 'uiToolkitV1/Button';
import { BUTTON_COLORS } from 'uiToolkitV1/Button/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { ListItemComp } from 'uiToolkitV1/ListItemComp';
import { TagColored } from 'uiToolkitV1/Tag';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { showErrorMessage, showLoader } from '../components/common';
import { calcInfoChipTextAndColor, getDerivedRowData, isNominated } from '../helpers';
import { getDeadlineEODTimeStamp } from './components/ReviewsPendingOnOthersComps/ReviewRowDisplay';
import { generatePendingOnMeOptions, getIsPendingForAcknowledgement } from './utils';

const TABLE_COLUMNS_DISPLAYNAME = {
  REVIEW_FOR: 'Review For',
  REVIEW_TYPE: 'Review Type',
  DUE_DATE: 'Due Date',
  REVIEW_CYCLE: 'Review Cycle',
  INSTANCE: 'Instance',
  STATUS: 'Status',
  EXPANDED_MENU: 'Expanded Menu',
};

// need to do this because agGrid `valueFormatter` doesnt have any impact on exports
function getFormattedValue({ value, api, column, node }) {
  const displayName = api.getDisplayNameForColumn(column);
  const rowData = node.data;
  const cellId = column.getColId();
  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FOR: {
      const cellData = rowData[cellId];
      return cellData?.value?.displayName;
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[value];
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      const { deadline } = Object.values(rowData)[0]?.reviewItem ?? {};
      const deadlineTS = parseISO(deadline);
      return isValid(deadlineTS) ? format(deadlineTS, 'yyyy-MM-dd') : 'NA';
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return value;
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      let infoChipText = '';
      const { review, isRequiredNominationsComplete, nominationsCount, requiredNominationsCount } =
        Object.values(rowData)[0]?.reviewItem ?? {};
      const derivedNominationsCount = nominationsCount ?? 0;
      const derivedRequiredNominationsCount = requiredNominationsCount ?? 0;

      const isNominatedReview = isNominated(review);

      const { isPendingForAcknowledgement } = getDerivedRowData({
        reviewStatus: rowData[6]?.value,
      });

      if (isNominatedReview) {
        infoChipText = 'Nominated';
      } else if (isPendingForAcknowledgement) {
        infoChipText = 'Published';
      } else if (review.providerType === 'NOMINATOR') {
        infoChipText = !isRequiredNominationsComplete ? `* ${infoChipText}` : infoChipText;
        infoChipText += derivedNominationsCount === 0 ? 'Nominate: ' : 'Nominated: ';
        infoChipText += derivedNominationsCount;
        infoChipText += derivedRequiredNominationsCount > 0 ? `/${derivedRequiredNominationsCount}` : '';
      } else {
        infoChipText = REVIEW_SUBMISSION_STATUS_MAP[review.submissionStatus].statusText;
      }
      return infoChipText;
    }

    default:
      return new Error('Invalid column name');
  }
}

function getCellContent(displayName, cellId, rowData, value) {
  if (isMobileView()) {
    switch (displayName) {
      case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FOR: {
        const { deadline } = Object.values(rowData)[0]?.reviewItem ?? {};
        const deadlineTS = getDeadlineEODTimeStamp(deadline);
        const { infoChipColor, infoChipText } = calcInfoChipTextAndColor({
          reviewItem: Object.values(rowData)[0]?.reviewItem,
          reviewStatus: rowData[6]?.value,
        });
        const reviewDisplayData = rowData[cellId];
        const reviewTypeData = rowData[2]?.value;
        const reviewTypeValue = REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP[reviewTypeData];

        return (
          <ReviewsMobileViewCell
            profilePhoto={reviewDisplayData?.value?.profilePhoto}
            name={reviewDisplayData?.value?.displayName}
            subTitle={reviewTypeValue}
            description={{
              title: 'Due',
              ...(deadlineTS && {
                dateProps: {
                  date: deadlineTS,
                },
              }),
              text: 'NA',
            }}
            tagProps={{ color: infoChipColor, text: infoChipText }}
          />
        );
      }
      default:
        return null; // Return null for other cases when mobileView is true
    }
  }
  switch (displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FOR: {
      const cellData = rowData[cellId];
      return (
        <ListItemComp
          title={cellData?.value?.displayName}
          LeftComponent={() => <Avatar src={cellData?.value?.profilePhoto} />}
          isBoldText={false}
          withoutBackground
        />
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return (
        <Box className="info" w="76px">
          <TextBodyMedium className="info">{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      const { deadline } = Object.values(rowData)[0]?.reviewItem ?? {};
      const deadlineTS = parseISO(deadline);
      return (
        <Box className="info">
          {isValid(deadlineTS) ? <DateComp date={deadlineTS} /> : <TextBodyMedium>NA</TextBodyMedium>}
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return (
        <Box className="info">
          <TextBodyMedium>{value}</TextBodyMedium>
        </Box>
      );
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      const { infoChipColor, infoChipText } = calcInfoChipTextAndColor({
        reviewItem: Object.values(rowData)[0]?.reviewItem,
        reviewStatus: rowData[6]?.value,
      });
      return (
        <Box className="info">
          <TagColored color={infoChipColor}>{infoChipText}</TagColored>
        </Box>
      );
    }

    default:
      return new Error('Invalid column name');
  }
}

function CellRenderer(params) {
  return getCellContent(params.colDef.displayName, params.colDef.id, params.data, params.value);
}

function getColProps(col) {
  const defaultColProps = {
    menuTabs: ['filterMenuTab', 'generalMenuTab'],
    headerComponent: CustomHeaderCell,
  };

  switch (col.displayName) {
    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_FOR: {
      return {
        ...defaultColProps,
        sortable: true,
        pinned: 'left',
        lockPosition: 'left',
        cellClass: 'locked-col',
        filter: 'agSetColumnFilter',
        valueGetter: (params) => params?.data[params.colDef.id]?.value.displayName,
        cellRenderer: CellRenderer,
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_TYPE: {
      return {
        ...defaultColProps,
        sortable: true,
        maxWidth: 220,
        filter: 'agSetColumnFilter',
        valueGetter: (params) => REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP?.[params?.data[params.colDef.id]?.value],
        cellRenderer: CellRenderer,
        filterValueGetter: (params) => REVIEW_RESPONSE_PROVIDER_TYPE_TITLE_MAP?.[params?.data[params.colDef.id]?.value],
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.DUE_DATE: {
      return {
        ...defaultColProps,
        sortable: true,
        maxWidth: 160,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agDateColumnFilter',
        filterValueGetter: ({ data }) => {
          if (!data[3]?.value) return null;
          return data[3]?.value;
        },
        filterParams: {
          comparator: agGridDateComparator,
        },
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.REVIEW_CYCLE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.INSTANCE: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agTextColumnFilter',
      };
    }

    case TABLE_COLUMNS_DISPLAYNAME.STATUS: {
      return {
        ...defaultColProps,
        sortable: true,
        valueGetter: (params) => params?.data[params.colDef.id]?.value,
        cellRenderer: CellRenderer,
        filter: 'agSetColumnFilter',
        filterValueGetter: (params) => {
          const { infoChipText } = calcInfoChipTextAndColor({
            reviewItem: Object.values(params.data)[0]?.reviewItem,
            reviewStatus: params.data[6]?.value,
          });
          return infoChipText;
        },
      };
    }

    default:
      return new Error('Invalid column name');
  }
}

const ReviewsPendingOnMeAGGrid = ({
  selectedTableRef,
  columns = [],
  rows = [],
  selectedTableId,
  loading,
  updatePeerStatus,
  resetData,
  errorMessage,
}) => {
  const [rowData, setRowData] = useState([]);

  const [columnDefs, setColumnDefs] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  const toast = useToast();

  useEffect(() => {
    if (columns.length === 0) return;

    if (isMobileView()) {
      const mobileColumnDefs = columns.slice(0, 1).map((col) => ({
        colId: col.id,
        field: col.id,
        id: col.id,
        displayName: col.displayName,
        minWidth: 255,
        flex: 1,
        headerComponent: CustomHeaderCell,
        headerComponentParams: {
          suppressHeaderMenuButton: true,
          enableSorting: false,
        },
        headerValueGetter: () => col.displayName,
        ...getColProps(col),
      }));
      setColumnDefs(mobileColumnDefs);
    } else {
      const newColumnDefs = columns.map((col) => {
        const defaultColsProps = {
          colId: col.id,
          field: col.id,
          id: col.id,
          displayName: col.displayName,
          minWidth: 220,
          hide: col.displayName === 'Instance',
          flex: 1,
          headerComponent: CustomHeaderCell,
          headerComponentParams: {
            enableMenu: true,
            enableSorting: true,
          },
          headerValueGetter: () => col.displayName,
          ...getColProps(col),
        };
        if (col.displayName === 'Review For' || col.displayName === 'Review Type') {
          return {
            ...defaultColsProps,
            lockVisible: true,
          };
        }
        return defaultColsProps;
      });
      setColumnDefs(newColumnDefs);
    }
  }, [columns]);

  useEffect(() => {
    setRowData(rows);
  }, [rows]);

  /* Grid State persistence Key */
  const gridStatePersistenceKey = useMemo(() => {
    const moduleName = MODULE_KEYS_USER_PREFERENCE_PERSISTENCE.REVIEW;
    const viewId = 'MyReviews';
    const tabId = 'Explore';
    const uniqueId = 'PendingOnMeGrid';

    return getPersistenceKey({
      moduleName,
      viewId,
      tabId,
      uniqueId,
    });
  }, []);

  const sideBar = useMemo(
    () => ({
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
          toolPanelParams: {
            suppressRowGroups: true,
            suppressValues: true,
            suppressPivots: true,
            suppressPivotMode: true,
            suppressColumnFilter: false,
            suppressColumnSelectAll: true,
            suppressColumnExpandAll: true,
          },
        },
      ],
      defaultToolPanel: '',
    }),
    []
  );

  const goToFormReviewRoute = (reviewId, employeeId) => {
    const rabbitRoute = getReviewFormRoute(reviewId, employeeId);
    const deerRoute = getDeerReviewFormRoute(reviewId, employeeId);
    const routeToGo = checkNewNavEnabled() ? `${deerRoute}?source=Me` : rabbitRoute;
    history.push(routeToGo);
  };

  const handleViewDetailsClick = (review) => {
    goToFormReviewRoute(review.feedbackForm.id, review.receiverEmployeeId);
    sendReviewFormPageViewEvent({ eventProperties: { source: 'Reviews pending on me' } });
  };

  const handleSubmissionStatusClick = ({ reviewItem }) => {
    const { review } = reviewItem;
    if (review.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      if (review.submissionStatus === REVIEW_FORM_COMPLETION_STATUS.NOT_STARTED) {
        sendClickedOnStartReviewEvent({
          eventProperties: {
            reviewCycleId: review.feedbackForm.id,
            empId: review.receiverEmployeeId,
            source: 'MyReviewsPendingOnMe',
          },
        });
      } else if (review.submissionStatus === REVIEW_FORM_COMPLETION_STATUS.IN_PROGRESS) {
        sendClickedOnContinueReviewEvent({
          eventProperties: {
            reviewCycleId: review.feedbackForm.id,
            empId: review.receiverEmployeeId,
            source: 'MyReviewsPendingOnMe',
          },
        });
      }
      handleViewDetailsClick(review);
    }
  };

  const handleAcknowledgeClick = ({ reviewItem }) => {
    const { review } = reviewItem;
    sendClickedOnAcknowledgeEvent({
      eventProperties: {
        reviewCycleId: review.feedbackForm.id,
        empId: review.receiverEmployeeId,
        source: 'MyReviewsPendingOnMe',
      },
    });
    if (review.summaryFilePath) {
      downloadOrOpenFile(review.summaryFilePath);
    } else {
      const routeToGo = checkNewNavEnabled()
        ? `${getDeerReviewResultRoute(
            review.feedbackForm.id,
            review.receiverEmployeeId
          )}?autoNavigate=true&source=Me&subSource=${selectedTableId}`
        : `${getReviewResultRoute(review.feedbackForm.id, review.receiverEmployeeId)}?autoNavigate=true`;
      history.push(routeToGo);
    }
  };

  const handleNominateClick = ({ reviewItem }) => {
    const { review, nominationsCount, endDateFormatted } = reviewItem;
    sendOpenedNominationPanelEvent({
      eventProperties: {
        reviewCycleId: review.id,
        source: 'MyReviewPendingOnMe',
        empId: loggedInEmployee.id,
      },
    });
    if (nominationsCount === 0) {
      sendClickedOnNominateEvent({
        eventProperties: {
          reviewCycleId: review.id,
          source: 'MyReviewPendingOnMe',
          empId: loggedInEmployee.id,
        },
      });
    } else {
      sendClickedOnEditNominationsEvent({
        eventProperties: {
          reviewCycleId: review.id,
          empId: review.receiverEmployeeId,
          source: 'MyReviewsPendingOnMe',
        },
      });
    }
    dispatch(
      showNominatePeers({
        reviewId: review.feedbackForm.id,
        onSubmit: resetData,
        nominationEndDateTS: endDateFormatted?.timeStamp,
      })
    );
  };

  if (loading) {
    return showLoader();
  }
  if (rows === null) {
    return showErrorMessage({ errorText: errorMessage });
  }
  if (rows.length === 0) {
    return (
      <div style={{ marginTop: '60px' }}>
        <EmptyScreen
          heading="There are no pending reviews"
          description="You don't have any pending reviews. Would you like to see a preview?"
          illustrationSrc={NoDataImg}
        />
      </div>
    );
  }

  const onCellClickHandler = ({ data: colData, column }) => {
    const {
      1: { reviewItem },
    } = colData;
    const { review } = reviewItem;

    const { isPendingForAcknowledgement } = getDerivedRowData({
      reviewStatus: colData[6]?.value,
    });

    if (column.colId === 'kebab-menu') {
      //  To prevent event bubbling.
    } else {
      let primaryActionRowClickHandler = handleSubmissionStatusClick;
      if (isPendingForAcknowledgement) {
        primaryActionRowClickHandler = handleAcknowledgeClick;
      } else if (reviewItem.review.providerType === 'NOMINATOR') {
        primaryActionRowClickHandler = handleNominateClick;
      } else if (isNominated(review)) {
        // because there are two action: accept and reject, don't keep a primary action on row click
        primaryActionRowClickHandler = noOp;
      }

      primaryActionRowClickHandler({ reviewItem });
    }
  };

  const getUIOptionsForLastColumn = (cellRendererData) => {
    const { review } = Object.values(cellRendererData)[0]?.reviewItem ?? {};
    const reviewStatus = cellRendererData[6]?.value;
    const isPendingForAcknowledgement = getIsPendingForAcknowledgement({ reviewStatus });
    const isUserNominated = isNominated(review);

    if (!isPendingForAcknowledgement && review.providerType !== 'NOMINATOR') {
      if (isUserNominated) {
        const options = generatePendingOnMeOptions({
          data: cellRendererData,
          dispatch,
          history,
          loggedInEmployee,
          resetData,
          toast,
          updatePeerStatus,
          isNewNavEnabled: checkNewNavEnabled(),
        });
        const [acceptOption, denyOption] = options;
        // eslint-disable-next-line react/no-unstable-nested-components
        const cellRenderer = () => (
          <Box
            display="flex"
            padding="16px"
            justifyContent="flex-end"
            alignItems="center"
            alignSelf="stretch"
            borderRadius="4px"
          >
            <Button StartIcon={CheckCircle} variant={BUTTON_VARIANT.tertiary} onClick={acceptOption.onClick}>
              Accept
            </Button>
            <Button
              StartIcon={CloseCircle}
              variant={BUTTON_VARIANT.tertiary}
              color={BUTTON_COLORS.red}
              onClick={denyOption.onClick}
            >
              Deny
            </Button>
          </Box>
        );
        return cellRenderer;
      }
    }

    return null;
  };

  const onGridReady = () => {
    if (selectedTableRef.current?.api) {
      selectedTableRef.current?.api.autoSizeColumns(['sidebarToggler']);
    }
  };

  // Setting width of last column on page change as for accept/deny review case we are showing buttons instead of kebab menu
  const onPaginationChanged = () => {
    if (selectedTableRef.current?.api) {
      selectedTableRef.current?.api.autoSizeColumns(['sidebarToggler']);
    }
  };

  // this is when we switch review cycles, setTimeout is used to delay till table has completed initial rendering
  const onRowDataUpdated = () => {
    if (selectedTableRef.current?.api) {
      setTimeout(() => selectedTableRef.current?.api.autoSizeColumns(['sidebarToggler']), 0);
    }
  };

  return (
    <Box className="review">
      <AgGridTable
        key={gridStatePersistenceKey} // we want to re-initialize on key change
        gridStatePersistenceKey={gridStatePersistenceKey}
        ref={selectedTableRef}
        rowData={rowData}
        columnDefs={columnDefs}
        domLayout="autoHeight"
        pagination
        suppressPaginationPanel={rowData?.length < 6 || rowData?.length === undefined || rowData?.length === 0}
        paginationPageSize={isMobileView() ? 5 : 10}
        sideBar={!isMobileView() && sideBar}
        headerHeight={36}
        autoHeight={false}
        rowHeight={isMobileView() ? 100 : 56}
        suppressCellFocus
        onCellClicked={onCellClickHandler}
        defaultCsvExportParams={{
          processCellCallback: getFormattedValue,
          fileName: 'Pending_on_me.csv',
          columnKeys: columns.map((c) => c.id),
        }}
        kebabMenuIsRowSpecific
        // eslint-disable-next-line react/no-unstable-nested-components
        kebabMenuOptionsGenerator={(data) => {
          const options = generatePendingOnMeOptions({
            data,
            dispatch,
            history,
            loggedInEmployee,
            resetData,
            toast,
            updatePeerStatus,
            isNewNavEnabled: checkNewNavEnabled(),
          });

          return options;
        }}
        getUIOptionsForLastColumn={(cellRendererData) => getUIOptionsForLastColumn(cellRendererData)}
        containerAutoHeight
        /* In Mobile view, we have only one column , so pinning it left is creating empty space in center */
        additionalMobileProps={{ pinned: undefined, lockPosition: undefined }}
        isMobileView={isMobileView()}
        onGridReady={onGridReady}
        onPaginationChanged={onPaginationChanged}
        onRowDataUpdated={onRowDataUpdated}
        paginationPageSizeSelector={isMobileView() ? false : [10, 20, 50, 100]}
      />
    </Box>
  );
};

export default ReviewsPendingOnMeAGGrid;
